import { CrudMode, FormItem, SelectsOptions } from '@ohpen/enso-ui';
import { FieldType, ProcurationFeeItem, ProcurationFeeItemType, FeeUnit } from '@pmp/api';
import type { FormInstance } from 'antd';
import type { FormListFieldData } from 'antd/es/form/FormList';
import { t } from 'i18next';
import { HALF_WIDTH, crudSection, procurationFeeRedirectPath, procurationFeeRuleRedirectPath } from '@pmp/utils';
import { renderReadonlyLinkedTag, renderReadonlyWithUnit } from '@pmp/components';

export const getLenderProcurationFeesFormSchema = (selectsOptions: SelectsOptions, mode?: CrudMode | null): FormItem[] => {
  const feeRuleColumns: FormItem[] =
    mode === CrudMode.VIEW
      ? [
          {
            title: 'lender.procurationFees.labels.operationType',
            dataIndex: 'operationType',
            valueType: 'select',
            tooltip: t('lender.procurationFees.labels.operationTypeTooltip'),
            fieldProps: {
              options: selectsOptions[FieldType.OPERATION_TYPE],
              disabled: true,
            },
          },
          {
            title: 'lender.procurationFees.labels.applicationType',
            dataIndex: 'applicationType',
            valueType: 'select',
            tooltip: t('lender.procurationFees.labels.applicationTypeTooltip'),
            fieldProps: {
              options: selectsOptions[FieldType.APPLICATION_TYPE],
              disabled: true,
            },
          },
          {
            title: 'lender.procurationFees.labels.propertyPurpose',
            dataIndex: 'propertyPurpose',
            valueType: 'select',
            tooltip: t('lender.procurationFees.labels.propertyPurposeTooltip'),
            fieldProps: {
              options: selectsOptions[FieldType.PROPERTY_PURPOSE],
              disabled: true,
            },
          },
        ]
      : [];

  const identityColumns: FormItem[] = [
    {
      title: 'lender.lender.labels.id',
      dataIndex: 'id',
      colProps: HALF_WIDTH,
      formItemProps: {
        hidden: mode === CrudMode.CREATE,
      },
      fieldProps: {
        disabled: true,
      },
    },
    {
      title: 'lender.lender.labels.order',
      dataIndex: 'order',
      colProps: HALF_WIDTH,
      tooltip: t('lender.lender.labels.orderInfo'),
      fieldProps: {
        disabled: true,
      },
    },
  ];

  const assignmentColumns: FormItem[] = [
    {
      title: 'lender.lender.labels.procurationFeeRule',
      dataIndex: 'procurationFeeRuleId',
      valueType: 'select',
      renderReadonly: renderReadonlyLinkedTag(procurationFeeRuleRedirectPath),
      formItemProps: {
        required: true,
      },
      fieldProps: {
        optionFilterProp: 'label',
        showSearch: true,
        options: selectsOptions[FieldType.PROCURATION_FEE_RULES],
        disabled: mode === CrudMode.VIEW,
      },
    },
    ...feeRuleColumns,
    {
      title: 'lender.lender.labels.procurationFee',
      dataIndex: 'procurationFeeId',
      valueType: 'select',
      renderReadonly: renderReadonlyLinkedTag(procurationFeeRedirectPath),
      formItemProps: {
        required: true,
      },
      fieldProps: {
        optionFilterProp: 'label',
        showSearch: true,
        options: selectsOptions[FieldType.PROCURATION_FEES],
        disabled: mode === CrudMode.VIEW,
      },
    },
  ];

  const sections = [
    crudSection('global.labels.general', mode, identityColumns),
    crudSection('lender.procurationFees.labels.procurationFee', mode, assignmentColumns),
  ];

  if (mode !== CrudMode.VIEW) {
    return sections;
  }

  return [
    ...sections,
    crudSection('lender.procurationFees.labels.fees', mode, [
      {
        dataIndex: 'feeItems',
        valueType: 'formList',
        listProps: {
          creatorButtonProps: false,
          deleteIconProps: false,
        },
        columns: (_meta: FormListFieldData, index: number, form: FormInstance) => {
          const feeItems = (form?.getFieldValue('feeItems') as ProcurationFeeItem[]) ?? [];
          const isCompletion = feeItems[index]?.procurationFeeItemType === ProcurationFeeItemType.COMPLETION;
          const isPercentage = feeItems[index]?.unit === FeeUnit.PERCENTAGE;

          const nonCompletionColumns: FormItem[] = isCompletion
            ? []
            : [
                {
                  dataIndex: 'startingMonth',
                  title: t('lender.lender.labels.startingMonth'),
                  valueType: 'digit' as const,
                  colProps: { span: 8 },
                  fieldProps: { disabled: true },
                },
                {
                  dataIndex: 'intervalInMonths',
                  title: t('lender.lender.labels.periodInMonths'),
                  valueType: 'digit' as const,
                  colProps: { span: 8 },
                  fieldProps: { disabled: true },
                },
                {
                  dataIndex: 'endingType',
                  title: t('lender.lender.labels.endingType'),
                  valueType: 'select' as const,
                  colProps: { span: 8 },
                  fieldProps: { options: selectsOptions[FieldType.ENDING_TYPE], disabled: true },
                },
              ];

          return [
            {
              dataIndex: 'procurationFeeItemType',
              title: t('lender.lender.labels.type'),
              valueType: 'select' as const,
              colProps: { span: 24 },
              fieldProps: { options: selectsOptions[FieldType.PROCURATION_FEE_ITEM_TYPE], disabled: true },
            },
            {
              title: t('lender.lender.labels.value'),
              dataIndex: 'value',
              valueType: isPercentage ? ('percent' as const) : ('digit' as const),
              colProps: { span: 12 },
              fieldProps: { disabled: true, addonBefore: isPercentage ? '%' : '£' },
              renderReadonly: renderReadonlyWithUnit(isPercentage ? { suffix: '%' } : { prefix: '£' }),
            },
            {
              dataIndex: 'basis',
              title: t('lender.lender.labels.basis'),
              valueType: 'select' as const,
              colProps: { span: 12 },
              fieldProps: { options: selectsOptions[FieldType.PROCURATION_FEE_BASIS], disabled: true },
            },
            ...nonCompletionColumns,
          ];
        },
      },
    ]),
  ];
};
